import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { Share } from "@capacitor/share";
import { KeyRound, Plus, Trash2, UserRound } from "lucide-react";
import { Card } from "@shared/ui/containers/Card";
import { Button } from "@shared/ui/primitives/Button";
import { Fab } from "@shared/ui/primitives/Fab";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { toast } from "@shared/ui/feedback/Toast";
import {
  cancelAccessInvite,
  fetchAccessInvites,
  type AccessInvite,
} from "./api";
import { pageCopy } from "@shared/lib/pageCopy";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { NewAccessInviteSheet } from "./NewAccessInvite";
import { cn } from "@shared/lib/cn";

type Tab = "qr" | "pin" | "plate";
const TABS: Array<{ id: Tab; label: string }> = [
  { id: "qr", label: "QR pass" },
  { id: "pin", label: "Door PIN" },
  { id: "plate", label: "Plate" },
];

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
});
const DEAD_STATUSES = new Set([
  "cancelled",
  "canceled",
  "expired",
  "revoked",
  "declined",
  "rejected",
]);

/** Format an ISO timestamp safely — never throws on a missing/invalid value. */
function fmtTime(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : TIME_FMT.format(d);
}

function validityLabel(invite: AccessInvite): string {
  const end = invite.valid_until ? new Date(invite.valid_until) : null;
  if (!end || Number.isNaN(end.getTime())) return "No expiry set";
  const sameDay = end.toDateString() === new Date().toDateString();
  return sameDay
    ? `Valid until today, ${TIME_FMT.format(end)}`
    : `Valid until ${end.toLocaleDateString(undefined, { day: "numeric", month: "short" })}, ${TIME_FMT.format(end)}`;
}

/**
 * Visitor access — dark hero header up top, with the main pass and visit
 * lists rendered on the standard light app surface below.
 */
export function AccessInvitesList(): JSX.Element {
  const copy = pageCopy().accessInvites;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("qr");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [qr, setQr] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const focusInviteId = searchParams.get("invite");

  const query = useQuery({
    queryKey: ["access-invites"],
    queryFn: fetchAccessInvites,
  });
  const cancelMutation = useMutation({
    mutationFn: cancelAccessInvite,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["access-invites"] });
      toast.success("Invite cancelled");
    },
    onError: (error) =>
      toast.danger(
        "Couldn't cancel invite",
        error instanceof Error ? error.message : undefined,
      ),
  });

  const upcoming = useMemo(() => {
    const now = Date.now();
    return [...(query.data ?? [])]
      .filter((i) => {
        if (DEAD_STATUSES.has((i.status ?? "").toLowerCase())) return false;
        if (i.valid_until) {
          const end = new Date(i.valid_until).getTime();
          if (!Number.isNaN(end) && end < now) return false;
        }
        return true;
      })
      .sort((a, b) => (a.valid_from ?? "").localeCompare(b.valid_from ?? ""));
  }, [query.data]);

  const selected = useMemo(
    () =>
      upcoming.find((i) => i.access_invite_id === selectedId) ?? upcoming[0],
    [upcoming, selectedId],
  );
  const previous = useMemo(
    () =>
      [...(query.data ?? [])].filter(
        (invite) =>
          !upcoming.some(
            (row) => row.access_invite_id === invite.access_invite_id,
          ),
      ),
    [query.data, upcoming],
  );
  const hasActivePasses = upcoming.length > 0;

  useEffect(() => {
    if (!selected) {
      setQr("");
      return;
    }
    const payload = JSON.stringify({
      t: "visitor",
      id: selected.access_invite_id,
      ref: selected.reference,
    });
    QRCode.toDataURL(payload, { margin: 1, width: 320 })
      .then(setQr)
      .catch(() => setQr(""));
  }, [selected]);

  useEffect(() => {
    if (!focusInviteId) return;
    const exists = (query.data ?? []).some(
      (invite) => invite.access_invite_id === focusInviteId,
    );
    if (!exists) return;
    setSelectedId(focusInviteId);
    setTab("qr");
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [focusInviteId, query.data, searchParams, setSearchParams]);

  const onShare = async () => {
    if (!selected) return;
    try {
      await Share.share({
        title: "Visitor access",
        text: `Visitor pass for ${selected.visitor_name ?? "your visitor"}${
          selected.reference ? ` · ${selected.reference}` : ""
        }`,
      });
    } catch {
      /* user dismissed */
    }
  };

  const onCopy = async () => {
    if (!selected) return;
    const ref = selected.reference ?? selected.access_invite_id;
    try {
      await navigator.clipboard.writeText(ref);
      toast.success("Reference copied");
    } catch {
      toast.danger("Could not copy");
    }
  };

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        backTo="/app/home"
        tabs={
          hasActivePasses
            ? TABS.map((t) => ({ value: t.id, label: t.label }))
            : undefined
        }
        activeTab={hasActivePasses ? tab : undefined}
        onTabChange={hasActivePasses ? (value) => setTab(value) : undefined}
        tabsLabel={hasActivePasses ? "Access invite format" : undefined}
      />

      <div className="px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {query.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-72 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : query.isError ? (
          <Card noGrain className="p-4">
            <ErrorState onRetry={() => query.refetch()} />
          </Card>
        ) : !selected ? (
          <Card noGrain className="px-6 py-12 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
              <KeyRound className="h-7 w-7" strokeWidth={1.5} />
            </span>
            <p className="mt-3 font-display text-headline text-ink">
              No active passes
            </p>
            <p className="mt-1 text-body-sm text-ink-secondary">
              Create a visitor invite to generate a pass.
            </p>
            <Button
              type="button"
              variant="gold"
              className="mt-4"
              onClick={() => setNewOpen(true)}
            >
              New invite
            </Button>
          </Card>
        ) : (
          <>
            {/* ── Pass body ────────────────────────────────────────── */}
            {tab === "qr" ? (
              <Card noGrain className="relative px-5 py-6">
                {canCancel(selected) ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-4 top-4 h-9 w-9 bg-danger/8 text-danger hover:bg-danger/12"
                    aria-label="Cancel invite"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Cancel invite ${selected.reference ?? ""}?`,
                        )
                      ) {
                        cancelMutation.mutate(selected.access_invite_id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.85} />
                  </Button>
                ) : null}
                <div className="flex flex-col items-center">
                  {qr ? (
                    <img src={qr} alt="Visitor QR pass" className="h-56 w-56" />
                  ) : (
                    <div className="h-56 w-56 animate-skeleton-pulse rounded bg-sunken" />
                  )}
                </div>
                <p className="mt-4 text-center text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
                  For {selected.visitor_name ?? "your visitor"}
                </p>
                <p className="display-serif text-center text-headline text-ink">
                  {validityLabel(selected)}
                </p>

                <div className="mt-5 flex flex-wrap justify-center gap-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="min-w-[8.5rem]"
                    onClick={() => void onCopy()}
                  >
                    Copy
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="gold"
                    className="min-w-[8.5rem]"
                    onClick={() => void onShare()}
                  >
                    Share
                  </Button>
                </div>
              </Card>
            ) : tab === "pin" ? (
              <PassPlaceholder
                icon={<KeyRound className="h-8 w-8" strokeWidth={1.5} />}
                title="Door PIN"
                body="A keypad PIN is issued for this visitor at the gate. Open the pass to view it once approved."
              />
            ) : (
              <PassPlaceholder
                icon={<UserRound className="h-8 w-8" strokeWidth={1.5} />}
                title="Number plate"
                body="Add the visitor's vehicle plate to their invite for automatic plate recognition."
              />
            )}

            {/* ── Upcoming ─────────────────────────────────────────── */}
            <p className="mt-7 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
              Upcoming
            </p>
            <div className="mt-2 flex flex-col gap-2">
              {upcoming.map((invite) => {
                const on =
                  invite.access_invite_id === selected.access_invite_id;
                const subline = invite.visitor_phone ?? invite.reference;
                return (
                  <Card
                    key={invite.access_invite_id}
                    asChild
                    noGrain
                    className={cn(
                      "px-3 py-3 text-left transition-colors",
                      on ? "bg-primary-50" : "hover:bg-surface",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(invite.access_invite_id)}
                      className="flex w-full items-center gap-3"
                    >
                      <span
                        className={cn(
                          "flex h-9 w-9 shrink-0 items-center justify-center rounded-pill",
                          on
                            ? "bg-primary-100 text-primary-700"
                            : "bg-sunken text-ink-muted",
                        )}
                      >
                        <UserRound className="h-4.5 w-4.5" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-body text-ink truncate">
                          {invite.visitor_name ?? "Visitor"}
                        </p>
                        {subline ? (
                          <p className="text-caption text-ink-muted truncate">
                            {subline}
                          </p>
                        ) : null}
                      </div>
                      {fmtTime(invite.valid_from) ? (
                        <span className="shrink-0 text-caption text-ink-secondary">
                          {fmtTime(invite.valid_from)}
                        </span>
                      ) : null}
                    </button>
                  </Card>
                );
              })}
            </div>

            {previous.length > 0 ? (
              <>
                <p className="mt-7 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-ink-muted">
                  Previous
                </p>
                <div className="mt-2 flex flex-col gap-2">
                  {previous.slice(0, 6).map((invite) => (
                    <Card
                      key={invite.access_invite_id}
                      noGrain
                      className="flex items-center gap-3 px-3 py-3 text-left"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink-muted">
                        <UserRound className="h-4.5 w-4.5" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-body text-ink truncate">
                          {invite.visitor_name ?? "Visitor"}
                        </p>
                        <p className="text-caption text-ink-muted truncate">
                          {inviteStatusLabel(invite)}
                          {invite.reference ? ` · ${invite.reference}` : ""}
                        </p>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>

      <Fab draggable onClick={() => setNewOpen(true)} label="New invite">
        <Plus className="h-5 w-5" strokeWidth={2.5} />
      </Fab>

      <NewAccessInviteSheet
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(invite) => {
          setSelectedId(invite.access_invite_id);
          setTab("qr");
          setNewOpen(false);
        }}
      />
    </div>
  );
}

function inviteStatusLabel(invite: AccessInvite): string {
  const status = (invite.status ?? "").toLowerCase();
  if (status === "accepted" || invite.accepted_at) return "Accepted";
  if (status === "cancelled" || status === "canceled") return "Cancelled";
  if (invite.valid_until) {
    const end = new Date(invite.valid_until).getTime();
    if (!Number.isNaN(end) && end < Date.now()) return "Expired";
  }
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Previous";
}

function canCancel(invite: AccessInvite): boolean {
  const status = (invite.status ?? "").toLowerCase();
  if (status === "accepted" || status === "cancelled" || status === "canceled")
    return false;
  return status === "draft" || status === "pending" || !status;
}

function PassPlaceholder({
  icon,
  title,
  body,
}: {
  icon: JSX.Element;
  title: string;
  body: string;
}): JSX.Element {
  return (
    <Card noGrain className="px-6 py-10 text-center">
      <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-pill bg-primary-50 text-primary-700">
        {icon}
      </span>
      <p className="mt-3 font-display text-headline text-ink">{title}</p>
      <p className="mt-1 text-body-sm text-ink-secondary">{body}</p>
    </Card>
  );
}
